/* Reuses native provider widgets; never calculates or hardcodes an offer. */
(() => {
  if (customElements.get('echelon-payment-options')) return;

  class EchelonPaymentOptions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.closest('.product-section__individual');
      this.launch = this.querySelector('[data-payment-options-open]');
      this.template = this.querySelector('[data-payment-options-template]');
      if (!this.root || !this.launch || !this.template) return;
      try {
        this.variants = JSON.parse(this.querySelector('[data-payment-options-variants]').textContent);
      } catch (_) {
        return;
      }
      this.onKeydown = this.onKeydown.bind(this);
      this.onVariant = this.onVariant.bind(this);
      this.onSectionUnload = this.onSectionUnload.bind(this);
      this.launch.addEventListener('click', () => this.open());
      this.root.addEventListener('variantChange', this.onVariant);
      this.root.addEventListener('change', this.onVariant);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      this.launch.hidden = false;
    }

    mount() {
      if (this.overlay) return true;
      this.shop = this.root.querySelector('.pdp-shop-pay-installments shopify-payment-terms');
      this.echelon = this.root.querySelector('.pdp-secondary-financing .ca-promotional-widget');
      if (!this.shop || !this.echelon) return false;
      this.shopMarker = document.createComment('payment-options-shop-original-position');
      this.echelonMarker = document.createComment('payment-options-echelon-original-position');
      this.shop.before(this.shopMarker);
      this.echelon.before(this.echelonMarker);
      const fragment = this.template.content.cloneNode(true);
      this.overlay = fragment.querySelector('.pdp-payment-options__overlay');
      this.panel = this.overlay.querySelector('[role="dialog"]');
      this.closeButton = this.overlay.querySelector('[data-payment-options-close]');
      this.overlay.addEventListener('click', event => {
        if (event.target === this.overlay || event.target.closest('[data-payment-options-close]')) this.close();
        const button = event.target.closest('[data-payment-options-provider]');
        if (!button || button.disabled) return;
        const native = this.providerButton(button.dataset.paymentOptionsProvider);
        // Keep the chooser and widget connected while the provider opens its own dialog.
        if (native) native.click();
      });
      document.body.append(fragment);
      return true;
    }

    providerButton(provider) {
      return provider === 'shop'
        ? this.shop?.shadowRoot?.querySelector('button[aria-haspopup="dialog"]')
        : this.echelon?.querySelector('button.ca-promotional-widget-link');
    }

    refreshActions(attempt = 0) {
      if (!this.overlay || this.overlay.hidden) return;
      let ready = true;
      this.overlay.querySelectorAll('[data-payment-options-provider]').forEach(button => {
        button.disabled = !this.providerButton(button.dataset.paymentOptionsProvider);
        if (button.disabled) ready = false;
      });
      if (!ready && attempt < 40) {
        this.retry = setTimeout(() => this.refreshActions(attempt + 1), 250);
      }
    }

    updatePrice() {
      const price = this.root.querySelector('.individual_product__price [data-product-price]');
      if (price?.textContent.trim()) {
        this.overlay.querySelector('[data-payment-options-price]').textContent = price.textContent.trim();
      }
    }

    onVariant(event) {
      const id = String(event.detail?.variant?.id || event.target.value || '');
      const variant = this.variants.find(item => String(item.id) === id);
      if (!variant) return; // Membership IDs must never update equipment financing.
      if (this.shop) this.shop.setAttribute('variant-id', String(variant.id));
      // Existing theme handlers remain authoritative for ChargeAfter and the visible price.
      if (this.overlay && !this.overlay.hidden) {
        requestAnimationFrame(() => this.updatePrice());
      }
    }

    open() {
      if (!this.mount() || !this.overlay.hidden) return;
      this.previousOverflow = document.body.style.overflow;
      this.overlay.querySelector('[data-payment-options-shop]').append(this.shop);
      this.overlay.querySelector('[data-payment-options-echelon]').append(this.echelon);
      this.updatePrice();
      this.overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      this.inerted = [];
      this.inertSiblings();
      this.bodyObserver = new MutationObserver(() => this.inertSiblings());
      this.bodyObserver.observe(document.body, { childList: true });
      document.addEventListener('keydown', this.onKeydown);
      this.closeButton.focus();
      this.refreshActions();
    }

    inertSiblings() {
      // Exempt native provider portals, including portals retained after first use.
      for (const element of document.body.children) {
        if (element === this.overlay || element.inert || ['SCRIPT', 'STYLE'].includes(element.tagName)) continue;
        if (element.matches('[data-nametag="shop-portal-provider"], iframe[title="Payment option flow"]')) continue;
        element.inert = true;
        this.inerted.push(element);
      }
    }

    nativeDialogOpen() {
      const shopPortals = document.querySelectorAll('[data-nametag="shop-portal-provider"]');
      if ([...shopPortals].some(portal => portal.shadowRoot?.querySelector('[data-testid="authorize-modal"][data-visible="true"]'))) return true;
      return [...document.querySelectorAll('iframe[title="Payment option flow"]')].some(frame => {
        const style = getComputedStyle(frame);
        return style.display !== 'none' && style.visibility !== 'hidden' && frame.getClientRects().length > 0;
      });
    }

    close(restoreFocus = true) {
      if (!this.overlay || this.overlay.hidden) return;
      clearTimeout(this.retry);
      this.bodyObserver?.disconnect();
      this.shopMarker.after(this.shop);
      this.echelonMarker.after(this.echelon);
      this.overlay.hidden = true;
      document.body.style.overflow = this.previousOverflow;
      this.inerted.forEach(element => { element.inert = false; });
      this.inerted = [];
      document.removeEventListener('keydown', this.onKeydown);
      if (restoreFocus && this.launch.isConnected) this.launch.focus();
    }

    onKeydown(event) {
      // Native provider dialogs own keyboard handling while focus is in their portal.
      if (event.defaultPrevented || this.nativeDialogOpen() || !event.composedPath().includes(this.overlay)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
      if (event.key !== 'Tab') return;
      const items = [this.closeButton];
      this.overlay.querySelectorAll('button:not([disabled]), shopify-payment-terms').forEach(element => {
        if (element === this.closeButton) return;
        if (element.shadowRoot) items.push(...element.shadowRoot.querySelectorAll('button:not([disabled])'));
        else items.push(element);
      });
      const active = this.shop.shadowRoot?.activeElement || document.activeElement;
      if (event.shiftKey && active === items[0]) {
        event.preventDefault();
        items[items.length - 1]?.focus();
      } else if (!event.shiftKey && active === items[items.length - 1]) {
        event.preventDefault();
        items[0]?.focus();
      }
    }

    onSectionUnload(event) {
      if (event.target.contains(this)) this.cleanup();
    }

    cleanup() {
      this.close(false);
      this.overlay?.remove();
      this.shopMarker?.remove();
      this.echelonMarker?.remove();
      this.root?.removeEventListener('variantChange', this.onVariant);
      this.root?.removeEventListener('change', this.onVariant);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
    }

    disconnectedCallback() {
      this.cleanup();
    }
  }
  customElements.define('echelon-payment-options', EchelonPaymentOptions);
})();
